import { graphql, withApollo } from 'react-apollo'

import { FormikProps, withFormik } from 'formik'
import { branch, compose, renderComponent, withHandlers, withProps, withState } from 'recompose'

import CenteredSpinner from 'components/CenteredSpinner'
import withPartnerProps from 'components/HOCs/WithPartnerProps'
import defaults from 'components/Trackers/defaults'
import bankCardOrderStatus from 'graphql-local/queries/cardOrderStatus.query.gql'
import trackEvent from 'utils/analytics/trackEvent'
import { withApolloErrorModal } from 'utils/Apollo'
import { sendToSentry } from 'utils/errors'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'

import { handleSubmit, mapPropsToValues, validate } from './form'
import updateAddressMutation from './graphql/updateAddress.mutation.gql'
import userQuery from './graphql/user.query.gql'
import {
  AddressProps,
  AddressType,
  EnhancedSpendContactInfoProps,
  GqlProps,
  HandlerProps,
  InnerProps,
  StateHandlerProps,
  StateProps,
} from './types'

export const enhance = compose<EnhancedSpendContactInfoProps, {}>(
  withApollo,
  withApolloErrorModal,
  withFlowRouter,
  withPartnerProps,
  withState('fromSpendConfirmation', 'setFromSpendConfirmation', ({ visitedPages }) =>
    visitedPages.some((path: string) => path === FlowRoute.SpendOrderConfirmation),
  ),
  withState('addressType', 'setAddressType', AddressType.Mailing),
  withState('submitAttempted', 'setSubmitAttempted', false),
  graphql(userQuery, {
    options: {
      fetchPolicy: 'network-only',
    },
  }),
  graphql(bankCardOrderStatus, {
    options: {
      fetchPolicy: 'network-only',
    },
    props: ({ data: { bankCardOrder } }) => {
      return {
        bankCardOrder,
      }
    },
  }),
  graphql(updateAddressMutation, { name: 'updateAddress' }),
  branch<GqlProps>(({ data: { loading } }) => loading, renderComponent(CenteredSpinner)),
  withProps<InnerProps, GqlProps & StateProps>(
    ({
      data: { user },
      bankCardOrder,
      addressType,
      flow,
      partner,
      route,
      fromSpendConfirmation,
    }) => {
      // If the user already has a Spend card order in any state other than 'notOrdered'
      // we will pass them along to the page proceeding the Spend order confirmation page.
      // If the user navigates back to this page to update information, it will not be captured
      // in their order as they'd expect.
      if (bankCardOrder && bankCardOrder.cardOrderStatus !== 'notOrdered') {
        route({ from: FlowRoute.ContactInfo, action: 'bank-card-ordered' })
      }

      if (addressType === AddressType.Mailing) {
        if (fromSpendConfirmation) {
          return {
            header: 'Shipping address',
            text: 'Let us know where to mail your card.',
          }
        } else {
          return {
            header: user.firstName ? `Nice to meet you, ${user.firstName}.` : 'Nice to meet you.',
            text: 'Where should we ship your new Acorns Visa debit card?',
          }
        }
      } else {
        trackEvent('Screen Viewed', {
          eventData: {
            ...defaults('Screen Viewed'),
            object_name: 'registrationInformationContactBilling',
            screen: 'registrationInformationContactBilling',
            screen_name: 'registrationInformationContactBilling',
          },
          partner,
          flow,
        })

        return {
          header: "What's your residential address?",
          text:
            "Since your residential address is different from your shipping address, we'll need that too.",
        }
      }
    },
  ),
  withHandlers<any, Partial<HandlerProps>>({
    saveAddress: ({ updateAddress }) => async (addressType: AddressType, values: AddressProps) => {
      // Construct an array of mutations that represent adddresses that need to be updated
      const updateMutations = []
      const input = {
        street1: values.address1,
        street2: values.address2,
        city: values.city,
        state: values.state,
        zipCode: values.zip,
        country: 'US',
      }

      // Persist Mailing address
      if (addressType === AddressType.Mailing) {
        updateMutations.push(
          updateAddress({
            variables: {
              addressType: 'mailing',
              input,
            },
          }),
        )
      }

      // Persist Residential address (or if alsoResidentialAddress box was checked)
      if (addressType === AddressType.Residential || values.alsoResidentialAddress) {
        updateMutations.push(
          updateAddress({
            variables: {
              addressType: 'residential',
              input,
            },
          }),
        )
      }

      return Promise.all(updateMutations).catch(error => {
        sendToSentry(error, {
          name: 'AddressUpdateError',
          message: "Customer's address could not be updated",
        })
      })
    },
  }),
  withFormik<{}, AddressProps, {}>({
    enableReinitialize: true,
    isInitialValid: true,
    mapPropsToValues,
    handleSubmit,
    validate,
    validateOnChange: false,
  }),
  withHandlers<FormikProps<AddressProps> & StateProps & StateHandlerProps, Partial<HandlerProps>>({
    handleChangeIntercept: ({ handleChange, submitAttempted, validateForm }) => async e => {
      await handleChange(e)

      // Only validate the form if the user has attempted at least one submit
      if (submitAttempted) {
        validateForm()
      }
    },
    handleSelectIntercept: ({ setFieldValue, submitAttempted, validateForm }) => async (
      field,
      value,
    ) => {
      await setFieldValue(field, value)

      // Only validate the form if the user has attempted at least one submit
      if (submitAttempted) {
        validateForm()
      }
    },
    handleSubmitIntercept: ({
      submitForm,
      submitAttempted,
      setSubmitAttempted,
      validateForm,
    }) => async () => {
      if (!submitAttempted) {
        await setSubmitAttempted(true)
        validateForm()
      }

      submitForm()
    },
  }),
)
